import { IChoiceGroupOption, MessageBarType } from '@fluentui/react';
import {
  Button,
  buttonStylesGhost,
  buttonStylesPrimary,
  ChoiceGroup,
  choiceGroupStylesHorizontalBox,
  hasChangedFieldValues,
  TextField,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { useCallback, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import { useDataSourceUpload, useTransformationUpload } from '../../featurestore/api';
import {
  CoreService,
  FeatureSetDraftRequest,
  FeatureSetMajorVersionDraftRequest,
  TransformationType,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import {
  buttonStylesPrimaryPanel,
  DataSourceFileInfo,
  DraftToFeatureSet,
  FeatureSetAction,
  TransformationInfo,
} from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { DataSourceInfo, SelectDataSource } from '../SelectDataSource/SelectDataSource';
import { TextListEditor } from '../TextListEditor/TextListEditor';
import {
  IRegisterFeatureSetSelectSchemaClassNames,
  IRegisterFeatureSetSelectSchemaStyles,
  registerFeatureSetSelectSchemaStylesDefault,
} from './RegisterFeatureSetSelectSchema.styles';

interface FeatureSetDraft extends FeatureSetDraftRequest {
  reason?: string;
  featureSetId?: string;
  featureSetVersion?: string;
  tempTransformationInfo?: TransformationInfo;
  tempDataSourceFileInfo?: DataSourceFileInfo;
}

enum Labels {
  manual = 'Manual',
  extractSchema = 'Extract Schema',
}

const options: IChoiceGroupOption[] = [
  { key: Labels.manual, text: Labels.manual },
  { key: Labels.extractSchema, text: Labels.extractSchema },
];

const getEmptyValues = (): FeatureSetDraft => {
  return {
    projectId: '',
    name: '',
    cred: undefined,
    dataSource: undefined,
    // register
    description: '',
    tags: [],
    // new version
    featureSetId: '',
    featureSetVersion: '',
    reason: '',
  };
};

const getCreationApi = (
  projectId: string,
  values: FeatureSetDraft,
  action: FeatureSetAction,
  featureSetId?: string,
  featureSetName?: string,
  featureSetVersion?: string
) => {
  const { description, tags, cred, dataSource, reason, name, derivedFrom } = values;
  switch (action) {
    case FeatureSetAction.register:
      const featureSetDraftRequest: FeatureSetDraftRequest = {
        projectId,
        name,
        description,
        tags,
        cred,
        dataSource,
        derivedFrom,
      };
      return fsApi(CoreService.CreateDraftFeatureSet, featureSetDraftRequest);
    case FeatureSetAction.newVersion:
      const featureSetMajorVersionDraftRequest: FeatureSetMajorVersionDraftRequest = {
        projectId,
        name: featureSetName,
        featureSetId,
        featureSetVersion,
        reason,
        cred,
        dataSource,
      };
      return fsApi(CoreService.CreateMajorVersionDraftFeatureSet, featureSetMajorVersionDraftRequest);
  }
  return undefined;
};

const isValid = (
  validDerivedInformation: boolean,
  action: FeatureSetAction,
  values: FeatureSetDraft,
  dataSourceInfo: DataSourceInfo
) => {
  if (validDerivedInformation) {
    if (action !== FeatureSetAction.newVersion) {
      return values.name?.trim() && hasChangedFieldValues(getEmptyValues(), { ...values, ...dataSourceInfo });
    }
    return true;
  }
  return false;
};

interface IRegisterFeatureSetSelectSchemaProps {
  action: FeatureSetAction;
  projectId: string;
  featureSetId?: string;
  featureSetName?: string;
  featureSetVersion?: string;
  draftFeatureSet?: DraftToFeatureSet;
  isNewVersionDraft?: boolean;
  changedFeatureSet?: boolean;
  onCreate: (draftFeatureSet?: FeatureSetDraft) => void;
  onMoveNext: (draftFeatureSet?: DraftToFeatureSet) => void;
  onCancel: () => void;
}

export const RegisterFeatureSetSelectSchema = ({
  action,
  projectId,
  featureSetId,
  featureSetName,
  featureSetVersion,
  draftFeatureSet = getEmptyValues(),
  isNewVersionDraft,
  changedFeatureSet,
  onCreate,
  onMoveNext,
  onCancel,
}: IRegisterFeatureSetSelectSchemaProps) => {
  const [optionLabel, setOptionLabel] = useState(Labels.manual);
  const [values, setValues] = useState<FeatureSetDraft>({ ...draftFeatureSet });
  const [dataSourceInfo, setDataSourceInfo] = useState<DataSourceInfo>({});
  const [validDerivedInformation, setValidDerivedInformation] = useState(true);
  const { getFileName } = useTransformationUpload();
  const { uploadDataSourceFiles } = useDataSourceUpload();
  const { addToast } = useToast();
  const onChange = useCallback((_e?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption) => {
    if (option) {
      setOptionLabel(option.key as Labels);
    }
  }, []);
  const createFeatureSet = useCallback(
    async (reqValues: FeatureSetDraft) => {
      const { tempTransformationInfo, tempDataSourceFileInfo } = reqValues;
      try {
        if (tempTransformationInfo) {
          const { featureSetIdVersion, md5Checksum, file, transformationType } = tempTransformationInfo;
          const filename = await getFileName(file, md5Checksum, transformationType);
          if (filename) {
            reqValues.derivedFrom = {
              featureSetIds: featureSetIdVersion ? [featureSetIdVersion] : undefined,
              transformation:
                transformationType === TransformationType.TransformationMojo
                  ? { mojo: { filename } }
                  : { sparkPipeline: { filename } },
            };
          }
        }
        const dataSource = await uploadDataSourceFiles(tempDataSourceFileInfo, reqValues.dataSource);
        if (dataSource && Object.keys(dataSource).length > 0) {
          reqValues.dataSource = dataSource;
        }
        await getCreationApi(projectId, reqValues, action, featureSetId, featureSetName, featureSetVersion);
        addToast({
          messageBarType: MessageBarType.success,
          message: 'The draft feature set has been created successfully!',
        });
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while creating the draft feature set: `));
      } finally {
        onCreate({ ...reqValues });
      }
    },
    [getFileName]
  );
  const onSubmit = useCallback(() => {
    if (optionLabel === Labels.extractSchema) {
      createFeatureSet({ ...values, ...dataSourceInfo });
    } else {
      onMoveNext({ ...values, projectId, id: featureSetId, featureSetVersion });
    }
  }, [values, optionLabel, dataSourceInfo]);
  const classNames = useClassNames<IRegisterFeatureSetSelectSchemaStyles, IRegisterFeatureSetSelectSchemaClassNames>(
    'RegisterFeatureSetSelectSchema',
    registerFeatureSetSelectSchemaStylesDefault
  );
  return (
    <div className={classNames.root} data-test="register-feature-set-schema">
      <div className={classNames.form}>
        <div className={classNames.fields}>
          {(action === FeatureSetAction.register || action === FeatureSetAction.draft) && !isNewVersionDraft && (
            <>
              <TextField
                required
                key="feature-set-name"
                name="name"
                label="Feature Set Name"
                value={values.name}
                onChange={(_e, name) => {
                  setValues({ ...values, name: name?.trimStart() });
                }}
              />
              <TextField
                name="description"
                label="Description"
                key="description"
                multiline
                rows={3}
                value={values.description}
                onChange={(_e, description) => {
                  setValues({ ...values, description });
                }}
              />
              <div className={classNames.subTitle} key="title-tags">
                Tags
              </div>
              <TextListEditor
                key="tag-list"
                placeholder="Input Tag with Enter"
                items={values.tags}
                onChange={(tags) => setValues({ ...values, tags })}
              />
            </>
          )}
          {(action === FeatureSetAction.newVersion || (action === FeatureSetAction.draft && isNewVersionDraft)) && (
            <TextField
              name="reason"
              label="Reason"
              key="reason"
              multiline
              rows={3}
              value={values.reason}
              onChange={(_e, reason) => {
                setValues({ ...values, reason });
              }}
            />
          )}
          {!changedFeatureSet && (
            <ChoiceGroup
              styles={choiceGroupStylesHorizontalBox}
              options={options}
              required
              label="Select how schema would be provided ?"
              key="schema-type"
              defaultSelectedKey={Labels.manual}
              onChange={onChange}
            />
          )}
          {optionLabel === Labels.extractSchema ? (
            <SelectDataSource
              hasTransformation={action === FeatureSetAction.register}
              hasUploadFile
              onChange={(dataSourceInfo, hasValidDerivedInformation) => {
                setValidDerivedInformation(hasValidDerivedInformation);
                setDataSourceInfo(dataSourceInfo);
              }}
            />
          ) : null}
        </div>
        <div className={classNames.buttons}>
          <div>
            <Button styles={buttonStylesGhost} text="Cancel" onClick={onCancel} style={{ marginRight: 8 }} />
            <Button
              styles={[buttonStylesPrimary, buttonStylesPrimaryPanel]}
              onClick={onSubmit}
              text={optionLabel === Labels.manual ? 'Next' : 'Save'}
              disabled={!isValid(validDerivedInformation, action, values, dataSourceInfo)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
